import { useSyncExternalStore } from "react";

// Persisted UI preference for which line `Amount` (components/amount.tsx)
// puts first when a `usd` value is shown: the crypto amount (default) or its
// USD estimate. A tiny external store (not React context) so `Amount` can
// read it from anywhere without every route needing to thread a provider —
// `SettingsPopover` is the only writer. Mirrors apps/merchant/src/lib/amount-order.ts.

export type AmountOrder = "crypto-first" | "fiat-first";

const STORAGE_KEY = "cpg_backoffice_amount_order";
const DEFAULT_ORDER: AmountOrder = "crypto-first";

type Listener = () => void;
const listeners = new Set<Listener>();

function isAmountOrder(value: string | null): value is AmountOrder {
  return value === "crypto-first" || value === "fiat-first";
}

export function getAmountOrder(): AmountOrder {
  if (typeof window === "undefined") return DEFAULT_ORDER;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isAmountOrder(stored) ? stored : DEFAULT_ORDER;
}

export function setAmountOrder(order: AmountOrder): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, order);
  } catch {
    // localStorage unavailable (e.g. private browsing) — setting just won't persist across reloads.
  }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Live-updates in every component using it the moment `setAmountOrder` is called anywhere, no prop drilling needed. */
export function useAmountOrder(): AmountOrder {
  return useSyncExternalStore(subscribe, getAmountOrder, () => DEFAULT_ORDER);
}
